import { Rng } from '../core/rng';
import { clamp } from '../core/util';
import { bondEffect, clampBond, CONCORD_GIFT, ELEMENT_OF, bondColor, type BondEffect, type BondTier } from '../data/elements';
import { BOSS_BY_ID, BOSS_ROSTER } from '../data/bosses';
import { COMMANDER_BY_ID } from '../data/commanders';
import { FAMILIES, FAMILY_BY_ID } from '../data/families';
import { LANE_LENGTH, LANE_SPEED_SCALE, laneSample } from '../data/lanes';
import { matchBosses, matchDeck, unitPower, type SideLoadout } from '../meta/profile';
import { LAYOUT, PAINTED_MOVE_COST, STANDARD_RULESET } from '../data/ruleset';
import { SHOP_BY_ID, SHOP_MAX_LEVEL } from '../data/shop';
import type {
  BossDefinition,
  Difficulty,
  Lane,
  MatchRulesetDefinition,
  MinionKind,
  ProjectileKind,
  ShopBranchId,
  Side,
  TowerFamilyDefinition,
  BoardMode,
} from '../data/types';

/** Fractions of a lane, measured from side 0's fortress toward side 1's. */
const LANE_POST_MARKS = [0.25, 0.5, 0.75];

// ---------------------------------------------------------------------------
// State types
// ---------------------------------------------------------------------------

export interface TowerState {
  id: number;
  side: Side;
  tile: number;
  familyId: string;
  rank: number;
  lane: Lane;
  spawnTimer: number;
  fireTimer: number;
  /** Seconds since last shot (drives the muzzle animation). */
  sinceShot: number;
  angle: number;
  targetId: number;
  bornAt: number;
  everSwitched: boolean;
  supportBonus: number;
  /** Element-bond deltas. Applied as max(0.45, 1 + delta). */
  bondDamage: number;
  bondFire: number;
  /** Visual: seconds since it was summoned / merged. */
  pulse: number;
}

export interface MinionState {
  id: number;
  side: Side;
  lane: Lane;
  pos: number;
  xOff: number;
  /** Column in the three-wide file. -1 until the first formation pass. */
  fileCol: number;
  familyId: string;
  faction: string;
  rank: number;
  kind: MinionKind;
  hp: number;
  maxHp: number;
  damage: number;
  attackInterval: number;
  range: number;
  speed: number;
  /** Shove strength. Heavier bodies push a lighter line back. */
  mass: number;
  attackTimer: number;
  specialTimer: number;
  slowTimer: number;
  slowFactor: number;
  /** While this is running, the minion's own hits are multiplied by weakFactor. */
  weakTimer: number;
  weakFactor: number;
  /** Ambush has already been spent. */
  opened: boolean;
  isBoss: boolean;
  bossName?: string;
  unstoppable: boolean;
  leak: number;
  scale: number;
  engaged: boolean;
  moving: boolean;
  /** 0 while held in a crowd, rising to 1 as they get going. Stops the lurch after a jam. */
  pace: number;
  shoved: boolean;
  age: number;
  hitFlash: number;
  special: Record<string, number>;
  bodySprite: 'body' | 'bodyHalf';
  /** Tower that mustered this minion. -1 for bosses. */
  sourceId: number;
  hpBare: number;
  damageBare: number;
  spitBare: number;
  blastBare: number;
  /** Element-bond deltas, same meaning as on towers. */
  bondHp: number;
  bondDamage: number;
  bondMarch: number;
}

export interface ProjectileState {
  id: number;
  side: Side;
  kind: ProjectileKind;
  x: number;
  y: number;
  sx: number;
  sy: number;
  targetId: number;
  tx: number;
  ty: number;
  lane: Lane;
  speed: number;
  damage: number;
  splash: number;
  slow: number;
  slowDuration: number;
  t: number;
  duration: number;
  pierceChance: number;
  pierced: boolean;
  /** On hit, the target's own attacks are multiplied by (1 - weaken). */
  weaken: number;
  weakenDuration: number;
  rotation: number;
}

export interface PoolState {
  id: number;
  side: Side;
  lane: Lane;
  pos: number;
  x: number;
  y: number;
  radius: number;
  heal: number;
  timer: number;
  duration: number;
  tick: number;
}

export interface PlayerStats {
  summons: number;
  merges: number;
  moves: number;
  laneSwitches: number;
  leaksTaken: [number, number];
  leaksDealt: [number, number];
  damageDealt: number;
  kills: number;
  bossKills: number;
  bossDamage: number;
  essenceEarned: number;
  essenceSpent: number;
  commanderUses: number;
  shopBuys: number;
  minionsMustered: [number, number];
  firstSwitchDelays: number[];
  highestRank: number;
  mergesByRegion: Record<string, number>;
}

export interface PlayerState {
  side: Side;
  isAi: boolean;
  hearts: number;
  essence: number;
  commanderId: string;
  commanderCooldown: number;
  commanderActive: number;
  wildcardArmed: boolean;
  shop: Record<ShopBranchId, number>;
  towers: (TowerState | null)[];
  stats: PlayerStats;
  incomeMultiplier: number;
  /** Five families this player can summon. */
  deck: string[];
  /** Three bosses this player can send. */
  bosses: string[];
}

export type SimEvent =
  | { type: 'summon'; side: Side; tile: number; familyId: string; x: number; y: number }
  | { type: 'merge'; side: Side; tile: number; familyId: string; rank: number; x: number; y: number }
  | { type: 'lane'; side: Side; tile: number; lane: Lane; x: number; y: number }
  | { type: 'shot'; side: Side; x: number; y: number; kind: ProjectileKind }
  | { type: 'hit'; x: number; y: number; kind: ProjectileKind | 'melee'; damage: number; side: Side }
  | { type: 'death'; x: number; y: number; side: Side; faction: string; isBoss: boolean; scale: number }
  | { type: 'leak'; side: Side; lane: Lane; amount: number; x: number; y: number }
  | { type: 'muster'; side: Side; tile: number; lane: Lane; x: number; y: number; tx: number; ty: number }
  | { type: 'bossWarning'; name: string; lanes: Lane[]; side: Side; description: string }
  | { type: 'bossSpawn'; name: string; x: number; y: number }
  | { type: 'pop'; x: number; y: number; rank: number }
  | { type: 'heal'; x: number; y: number; amount: number }
  | { type: 'explosion'; x: number; y: number; radius: number; kind: 'fire' | 'blast' | 'mortar' | 'frost' }
  | { type: 'commander'; side: Side; id: string; name: string }
  | { type: 'shop'; side: Side; branch: ShopBranchId; level: number }
  | { type: 'overtime' }
  | { type: 'essence'; side: Side; amount: number; x: number; y: number }
  | { type: 'shove'; x: number; y: number }
  | { type: 'ignore'; x: number; y: number }
  | { type: 'error'; side: Side; message: string }
  | { type: 'end'; winner: Side | -1; reason: string };

export interface TelemetryEvent {
  t: number;
  type: string;
  side?: Side;
  [key: string]: unknown;
}

export interface MatchConfig {
  seed: number;
  playerCommander: string;
  aiCommander: string;
  difficulty: Difficulty;
  ruleset?: MatchRulesetDefinition;
  /** Card levels and crit for each side. Missing means level 1 and no crit. */
  loadout?: [SideLoadout, SideLoadout];
  /** A human opponent uses the same income as you. Bots keep the difficulty curve. */
  opponent?: 'ai' | 'human';
  /** Arrows: tap to flip a lane. Painted: the cell color is the lane. */
  board?: BoardMode;
  /** Companies in sim order. Missing means a seeded deal, so both clients still match. */
  decks?: [string[], string[]];
  bosses?: [string[], string[]];
}

export type DropResult = 'merge' | 'move' | 'swap' | 'none';

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

export function tileCenter(side: Side, tile: number): { x: number; y: number } {
  const g = side === 0 ? LAYOUT.playerGrid : LAYOUT.enemyGrid;
  const col = tile % 5;
  const row = Math.floor(tile / 5);
  const step = g.tile + g.gap;
  const x = g.x + col * step + g.tile / 2;
  const visualRow = side === 0 ? row : 2 - row;
  const y = g.y + visualRow * step + g.tile / 2;
  return { x, y };
}

export function tileRect(side: Side, tile: number): { x: number; y: number; w: number; h: number } {
  const g = side === 0 ? LAYOUT.playerGrid : LAYOUT.enemyGrid;
  const c = tileCenter(side, tile);
  return { x: c.x - g.tile / 2, y: c.y - g.tile / 2, w: g.tile, h: g.tile };
}

export function minionXY(m: { side: Side; lane: Lane; pos: number; xOff: number }): { x: number; y: number } {
  const along = m.side === 0 ? m.pos : LANE_LENGTH - m.pos;
  const s = laneSample(m.lane, along);
  return { x: s.x + s.nx * m.xOff, y: s.y + s.ny * m.xOff };
}

export function laneEntry(side: Side, lane: Lane): { x: number; y: number } {
  const s = laneSample(lane, side === 0 ? 0 : LANE_LENGTH);
  return { x: s.x, y: s.y };
}

const DIFFICULTY_INCOME: Record<Difficulty, number> = { recruit: 0.72, veteran: 1.0, warlord: 1.22 };

// ---------------------------------------------------------------------------
// Match
// ---------------------------------------------------------------------------

interface AuraMods {
  minionHp: number;
  minionDamage: number;
  minionSpeed: number;
  towerDamage: number;
  fireRate: number;
  spawnRate: number;
  notes: string[];
}

interface BondHit {
  effect: BondEffect;
  meet: boolean;
  tileA: number;
  tileB: number;
}

export type BondForm = 'none' | 'bracket' | 'concord' | 'both';

export interface ElementLink {
  side: Side;
  tileA: number;
  tileB: number;
  tier: BondTier;
  color: string;
  doubled: boolean;
  /** Bracket is a straight Earth–Fire–Earth pattern. Concord is all four elements in one straight line. */
  form: BondForm;
}

export interface UnitPreview {
  name: string;
  role: string;
  rank: number;
  attack: string;
  damage: number;
  fire: number;
  muster: number;
  minionName: string;
  minionHp: number;
  minionDamage: number;
  minionSpeed: number;
  minionMass: number;
  about: string;
  minionAbout: string;
  notes: string[];
}

export class Match {
  readonly rules: MatchRulesetDefinition;
  readonly rng: Rng;
  readonly config: MatchConfig;
  readonly players: [PlayerState, PlayerState];
  minions: MinionState[] = [];
  projectiles: ProjectileState[] = [];
  pools: PoolState[] = [];
  time = 0;
  overtime = false;
  ended = false;
  winner: Side | -1 = -1;
  endReason = '';
  /** Neighbor bonds drawn between towers. Rebuilt every step. */
  elementLinks: ElementLink[] = [];
  /** Boss auras never change during a match, so the result is kept. */
  private auraCache: [AuraMods | null, AuraMods | null] = [null, null];
  /** A tower mustered this step, so bonds are rebuilt once more for the new body. */
  private mustered = false;
  /** Minions grouped by the tower that sent them. Rebuilt with the bonds. */
  private sourceGroups = new Map<number, MinionState[]>();
  /** World position of each living minion, filled once per step for aiming. */
  private aimPoints = new Map<number, { x: number; y: number }>();
  private aimTick = -1;
  events: SimEvent[] = [];
  telemetry: (e: TelemetryEvent) => void = () => {};
  private nextId = 1;
  /** Completed boss marches. Wave 1 is the opening; pay rises after each march. */
  private bossWave = 0;
  private bossWarned = false;
  /** The one boss each side will send, and which lane it takes. */
  private nextMarch: [{ id: string; lane: Lane }, { id: string; lane: Lane }] = [
    { id: 'warden', lane: 0 },
    { id: 'warden', lane: 1 },
  ];
  readonly bossEvery = 90;
  /** No ordinary minions muster until this time. Set for 5s after a boss appears. */
  private musterHoldUntil = 0;
  readonly board: BoardMode;
  /** Lane color of each cell. Empty unless the board is painted. Shared by both sides. */
  readonly cellLanes: Lane[] = [];
  private tick = 0;

  constructor(config: MatchConfig) {
    this.config = config;
    this.rules = config.ruleset ?? STANDARD_RULESET;
    this.rng = new Rng(config.seed);
    const human = config.opponent === 'human';
    this.players = [
      this.makePlayer(0, config.playerCommander, false, 1),
      this.makePlayer(1, config.aiCommander, !human, human ? 1 : DIFFICULTY_INCOME[config.difficulty]),
    ];
    this.dealLoadouts();
    this.board = config.board ?? 'arrows';
    if (this.board === 'painted') this.cellLanes = this.rollCells();
  }

  /** 1 during the opening, then one higher after every boss march. */
  waveNumber(): number {
    return this.bossWave + 1;
  }

  /** How much a minion kill pays compared with the opening wave. A small step, not the old +50% per boss. */
  wavePay(): number {
    return 1 + 0.12 * this.bossWave;
  }

  /**
   * Three posts per lane, measured from side 0's fortress.
   * A post is held only while that side's front is past it and the other front has not crossed it.
   */
  lanePosts(): Array<{ lane: Lane; distance: number; owner: Side | null }> {
    const posts: Array<{ lane: Lane; distance: number; owner: Side | null }> = [];
    for (const lane of [0, 1] as Lane[]) {
      const reach = this.laneReach(lane);
      for (const mark of LANE_POST_MARKS) {
        const distance = mark * LANE_LENGTH;
        posts.push({ lane, distance, owner: this.postOwner(reach, distance) });
      }
    }
    return posts;
  }

  private laneReach(lane: Lane): { w0: number | null; w1: number | null } {
    let w0: number | null = null;
    let w1: number | null = null;
    for (const m of this.minions) {
      if (m.hp <= 0 || m.lane !== lane) continue;
      if (m.side === 0) w0 = w0 === null ? m.pos : Math.max(w0, m.pos);
      else {
        const w = LANE_LENGTH - m.pos;
        w1 = w1 === null ? w : Math.min(w1, w);
      }
    }
    return { w0, w1 };
  }

  private postOwner(reach: { w0: number | null; w1: number | null }, distance: number): Side | null {
    const side0 = reach.w0 !== null && reach.w0 >= distance;
    const side1 = reach.w1 !== null && reach.w1 <= distance;
    if (side0 && !side1) return 0;
    if (side1 && !side0) return 1;
    return null;
  }

  private postCount(side: Side): number {
    let n = 0;
    for (const post of this.lanePosts()) if (post.owner === side) n++;
    return n;
  }

  /** Seconds until the next field-burst. Zero once Sudden Death has started. */
  nextBossIn(): number {
    if (this.overtime) return 0;
    const due = (this.bossWave + 1) * this.bossEvery;
    if (due >= this.rules.overtimeAt) return Math.max(0, this.rules.overtimeAt - this.time);
    return Math.max(0, due - this.time);
  }

  private dealLoadouts(): void {
    const decks = this.config.decks;
    const bosses = this.config.bosses;
    if (decks && bosses) {
      this.players[0].deck = matchDeck(decks[0]);
      this.players[1].deck = matchDeck(decks[1]);
      this.players[0].bosses = matchBosses(bosses[0]);
      this.players[1].bosses = matchBosses(bosses[1]);
      return;
    }
    const families = this.rng.shuffle(FAMILIES.map((f) => f.id));
    const roster = this.rng.shuffle(BOSS_ROSTER.map((b) => b.id));
    this.players[0].deck = families.slice(0, 5);
    this.players[1].deck = [...families.slice(1), families[0]].slice(0, 5);
    this.players[0].bosses = roster.slice(0, 3);
    this.players[1].bosses = roster.slice(3, 6);
  }

  /** One boss from the three, and a random lane. The two sides pick independently. */
  private pickMarch(ids: string[]): { id: string; lane: Lane } {
    const pool = ids.filter((id) => BOSS_BY_ID[id]);
    const bag = this.rng.shuffle(pool.length ? pool : ['warden']);
    return { id: bag[0], lane: this.rng.chance(0.5) ? 0 : 1 };
  }

  /** At least five cells of each color, so both lanes stay playable. */
  private rollCells(): Lane[] {
    const cells: Lane[] = [];
    for (let i = 0; i < 15; i++) cells.push(this.rng.chance(0.5) ? 0 : 1);
    const count = (lane: Lane): number => cells.filter((c) => c === lane).length;
    let guard = 0;
    while ((count(0) < 5 || count(1) < 5) && guard++ < 20) {
      const lean: Lane = count(0) > count(1) ? 0 : 1;
      const idx = cells.findIndex((c) => c === lean);
      if (idx >= 0) cells[idx] = lean === 0 ? 1 : 0;
    }
    return cells;
  }

  private deckOf(side: Side): TowerFamilyDefinition[] {
    return this.players[side].deck.map((id) => FAMILY_BY_ID[id]);
  }

  private makePlayer(side: Side, commanderId: string, isAi: boolean, incomeMultiplier: number): PlayerState {
    return {
      side,
      isAi,
      hearts: this.rules.fortressHearts,
      essence: this.rules.startEssence,
      commanderId,
      commanderCooldown: 0,
      commanderActive: 0,
      wildcardArmed: false,
      shop: { barrage: 0, tempo: 0, muster: 0, greed: 0 },
      towers: new Array(this.rules.columns * this.rules.rows).fill(null),
      incomeMultiplier,
      deck: [],
      bosses: [],
      stats: {
        summons: 0,
        merges: 0,
        moves: 0,
        laneSwitches: 0,
        leaksTaken: [0, 0],
        leaksDealt: [0, 0],
        damageDealt: 0,
        kills: 0,
        bossKills: 0,
        bossDamage: 0,
        essenceEarned: 0,
        essenceSpent: 0,
        commanderUses: 0,
        shopBuys: 0,
        minionsMustered: [0, 0],
        firstSwitchDelays: [],
        highestRank: 0,
        mergesByRegion: {},
      },
    };
  }

  // ------------------------------------------------------------------ queries

  summonCost(side: Side): number {
    const p = this.players[side];
    if (p.wildcardArmed) return 0;
    const growth = p.commanderId === 'oda' ? 0.7 : 1;
    const raw = this.rules.summonBaseCost + this.rules.summonCostPerTower * p.stats.summons * growth;
    return Math.round(raw * (1 - 0.06 * p.shop.greed));
  }

  shopCost(side: Side, branch: ShopBranchId): number | null {
    const lvl = this.players[side].shop[branch];
    if (lvl >= SHOP_MAX_LEVEL) return null;
    return SHOP_BY_ID[branch].costs[lvl];
  }

  emptyTiles(side: Side): number[] {
    const out: number[] = [];
    this.players[side].towers.forEach((t, i) => {
      if (!t) out.push(i);
    });
    return out;
  }

  /** An empty cell sharing a side with a tower, so a summon can form a bond. */
  private tileBeside(side: Side, empties: number[]): number {
    const towers = this.players[side].towers;
    const beside = empties.filter((tile) => {
      const col = tile % 5;
      const row = Math.floor(tile / 5);
      const neighbors = [
        col > 0 ? tile - 1 : -1,
        col < 4 ? tile + 1 : -1,
        row > 0 ? tile - 5 : -1,
        row < 2 ? tile + 5 : -1,
      ];
      return neighbors.some((n) => n >= 0 && towers[n]);
    });
    return this.rng.pick(beside.length > 0 ? beside : empties);
  }

  towersOf(side: Side): TowerState[] {
    return this.players[side].towers.filter((t): t is TowerState => t !== null);
  }

  /** Pairs of tiles that can be merged (same family, same rank below max). */
  mergePairs(side: Side): Array<[number, number, number]> {
    const towers = this.towersOf(side);
    const pairs: Array<[number, number, number]> = [];
    for (let i = 0; i < towers.length; i++) {
      for (let j = i + 1; j < towers.length; j++) {
        const a = towers[i];
        const b = towers[j];
        if (a.familyId === b.familyId && a.rank === b.rank && a.rank < this.rules.maxRank) {
          pairs.push([a.tile, b.tile, a.rank]);
        }
      }
    }
    return pairs;
  }

  /** Living minions of `side` in each lane. Corpses waiting to be removed do not count. */
  laneCounts(side: Side): [number, number] {
    const c: [number, number] = [0, 0];
    for (const m of this.minions) if (m.side === side && m.hp > 0) c[m.lane]++;
    return c;
  }

  /** Total hp of enemy minions threatening `side` per lane, weighted by how far they have marched. */
  lanePressure(side: Side): [number, number] {
    const p: [number, number] = [0, 0];
    for (const m of this.minions) {
      if (m.side !== side) p[m.lane] += m.hp * (0.5 + 0.5 * (m.pos / LANE_LENGTH));
    }
    return p;
  }

  towerLaneCounts(side: Side): [number, number] {
    const c: [number, number] = [0, 0];
    for (const t of this.towersOf(side)) c[t.lane]++;
    return c;
  }

  // ---------------------------------------------------------------- commands

  summon(side: Side, beside = false): boolean {
    if (this.ended) return false;
    const p = this.players[side];
    const empties = this.emptyTiles(side);
    if (empties.length === 0) {
      this.events.push({ type: 'error', side, message: 'Board is full - merge to make room' });
      return false;
    }
    const cost = this.summonCost(side);
    if (p.essence < cost) {
      this.events.push({ type: 'error', side, message: 'Not enough Essence' });
      return false;
    }
    let family: TowerFamilyDefinition;
    if (p.wildcardArmed) {
      const onBoard = this.towersOf(side);
      if (onBoard.length > 0) {
        const lowest = onBoard.reduce((a, b) => (b.rank < a.rank ? b : a));
        const candidates = onBoard.filter((t) => t.rank === lowest.rank);
        family = FAMILY_BY_ID[this.rng.pick(candidates).familyId];
      } else {
        family = this.rng.pick(this.deckOf(side));
      }
      p.wildcardArmed = false;
    } else {
      family = this.rng.pick(this.deckOf(side));
    }
    this.spend(p, cost);
    const tile = beside ? this.tileBeside(side, empties) : this.rng.pick(empties);
    const lane = this.defaultLane(side, tile);
    const tower: TowerState = {
      id: this.nextId++,
      side,
      tile,
      familyId: family.id,
      rank: 1,
      lane,
      spawnTimer: this.spawnInterval(side, family, 1, 0) * 0.6,
      fireTimer: 0.3,
      sinceShot: 10,
      angle: side === 0 ? -Math.PI / 2 : Math.PI / 2,
      targetId: -1,
      bornAt: this.time,
      everSwitched: false,
      supportBonus: 0,
      bondDamage: 0,
      bondFire: 0,
      pulse: 0,
    };
    p.towers[tile] = tower;
    p.stats.summons++;
    p.stats.highestRank = Math.max(p.stats.highestRank, 1);
    const c = tileCenter(side, tile);
    this.events.push({ type: 'summon', side, tile, familyId: family.id, x: c.x, y: c.y });
    this.telemetry({ t: this.time, type: 'summon', side, tile, family: family.id, lane, cost });
    return true;
  }

  private defaultLane(side: Side, tile: number): Lane {
    if (this.board === 'painted') return this.cellLanes[tile] ?? 0;
    const col = tile % 5;
    if (col <= 1) return 0;
    if (col >= 3) return 1;
    const counts = this.towerLaneCounts(side);
    if (counts[0] === counts[1]) return this.rng.chance(0.5) ? 0 : 1;
    return counts[0] < counts[1] ? 0 : 1;
  }

  toggleLane(side: Side, tile: number): boolean {
    if (this.ended || this.board === 'painted') return false;
    const t = this.players[side].towers[tile];
    if (!t) return false;
    t.lane = t.lane === 0 ? 1 : 0;
    const p = this.players[side];
    p.stats.laneSwitches++;
    if (!t.everSwitched) {
      t.everSwitched = true;
      p.stats.firstSwitchDelays.push(this.time - t.bornAt);
    }
    const c = tileCenter(side, tile);
    this.events.push({ type: 'lane', side, tile, lane: t.lane, x: c.x, y: c.y });
    this.telemetry({
      t: this.time,
      type: 'lane_switch',
      side,
      tile,
      family: t.familyId,
      rank: t.rank,
      lane: t.lane,
      sinceSummon: this.time - t.bornAt,
      pressure: this.lanePressure(side),
    });
    return true;
  }

  setLane(side: Side, tile: number, lane: Lane): boolean {
    const t = this.players[side].towers[tile];
    if (!t || t.lane === lane) return false;
    return this.toggleLane(side, tile);
  }

  /** Why a drop onto an occupied tile will not merge. Null when it will, or when the tiles are obviously different. */
  whyNotMerge(side: Side, from: number, to: number): string | null {
    if (from === to) return null;
    const a = this.players[side].towers[from];
    const b = this.players[side].towers[to];
    if (!a || !b) return null;
    if (a.familyId === b.familyId && a.rank === b.rank && a.rank < this.rules.maxRank) return null;
    if (a.familyId === b.familyId && a.rank === b.rank) return 'MAX RANK';
    if (a.familyId === b.familyId) return 'SAME RANK TO MERGE';
    const fa = FAMILY_BY_ID[a.familyId];
    const fb = FAMILY_BY_ID[b.familyId];
    if (fa && fb && fa.faction === fb.faction) return `${fb.name.toUpperCase()} IS A DIFFERENT UNIT`;
    return null;
  }

  canMerge(side: Side, from: number, to: number): boolean {
    if (from === to) return false;
    const a = this.players[side].towers[from];
    const b = this.players[side].towers[to];
    return !!a && !!b && a.familyId === b.familyId && a.rank === b.rank && a.rank < this.rules.maxRank;
  }

  /** Drag-and-drop resolution: merge, move to empty, or swap. */
  drop(side: Side, from: number, to: number): DropResult {
    if (this.ended || from === to) return 'none';
    const p = this.players[side];
    const a = p.towers[from];
    if (!a) return 'none';
    const b = p.towers[to];
    if (b && a.familyId === b.familyId && a.rank === b.rank && a.rank < this.rules.maxRank) {
      const others = p.deck.filter((id) => id !== b.familyId);
      b.familyId = this.rng.pick(others.length > 0 ? others : p.deck);
      b.rank += 1;
      b.pulse = 0;
      b.spawnTimer = Math.min(b.spawnTimer, a.spawnTimer);
      if (this.board === 'painted') b.lane = this.cellLanes[to] ?? b.lane;
      p.towers[from] = null;
      p.stats.merges++;
      p.stats.highestRank = Math.max(p.stats.highestRank, b.rank);
      const region = this.regionOf(to);
      p.stats.mergesByRegion[region] = (p.stats.mergesByRegion[region] ?? 0) + 1;
      const c = tileCenter(side, to);
      this.events.push({ type: 'merge', side, tile: to, familyId: b.familyId, rank: b.rank, x: c.x, y: c.y });
      this.telemetry({ t: this.time, type: 'merge', side, from, to, family: b.familyId, rank: b.rank, lane: b.lane, region });
      return 'merge';
    }
    if (this.board !== 'painted') {
      if (!b) this.events.push({ type: 'error', side, message: 'Tap a tower to switch its lane' });
      return 'none';
    }
    if (!this.chargeMove(p)) return 'none';
    if (!b) {
      p.towers[to] = a;
      p.towers[from] = null;
      a.tile = to;
      if (this.board === 'painted') a.lane = this.cellLanes[to] ?? a.lane;
      p.stats.moves++;
      this.telemetry({ t: this.time, type: 'move', side, from, to });
      return 'move';
    }
    p.towers[to] = a;
    p.towers[from] = b;
    a.tile = to;
    b.tile = from;
    if (this.board === 'painted') {
      a.lane = this.cellLanes[to] ?? a.lane;
      b.lane = this.cellLanes[from] ?? b.lane;
    }
    p.stats.moves++;
    this.telemetry({ t: this.time, type: 'swap', side, from, to });
    return 'swap';
  }

  private chargeMove(p: PlayerState): boolean {
    if (p.essence < PAINTED_MOVE_COST) {
      this.events.push({ type: 'error', side: p.side, message: 'Moving costs 5 Essence' });
      return false;
    }
    this.spend(p, PAINTED_MOVE_COST);
    return true;
  }

  private regionOf(tile: number): string {
    const row = Math.floor(tile / 5);
    const col = tile % 5;
    const rowName = row === 0 ? 'front' : row === 1 ? 'middle' : 'back';
    const colName = col <= 1 ? 'left' : col === 2 ? 'center' : 'right';
    return `${rowName}-${colName}`;
  }

  buyUpgrade(side: Side, branch: ShopBranchId): boolean {
    if (this.ended) return false;
    const p = this.players[side];
    const cost = this.shopCost(side, branch);
    if (cost === null) {
      this.events.push({ type: 'error', side, message: 'Already maxed' });
      return false;
    }
    if (p.essence < cost) {
      this.events.push({ type: 'error', side, message: 'Not enough Essence' });
      return false;
    }
    this.spend(p, cost);
    p.shop[branch]++;
    p.stats.shopBuys++;
    this.events.push({ type: 'shop', side, branch, level: p.shop[branch] });
    this.telemetry({ t: this.time, type: 'shop_buy', side, branch, level: p.shop[branch], cost });
    return true;
  }

  useCommander(side: Side): boolean {
    if (this.ended) return false;
    const p = this.players[side];
    if (p.commanderCooldown > 0) return false;
    const def = COMMANDER_BY_ID[p.commanderId];
    switch (p.commanderId) {
      case 'vael': {
        const pressure = this.lanePressure(side);
        const counts = this.laneCounts(side);
        // Lane with heaviest enemy pressure; if none, the lane where we have fewer minions.
        let lane: Lane;
        if (pressure[0] === 0 && pressure[1] === 0) lane = counts[0] <= counts[1] ? 0 : 1;
        else lane = pressure[0] >= pressure[1] ? 0 : 1;
        const towers = this.towersOf(side);
        if (towers.length === 0) {
          this.events.push({ type: 'error', side, message: 'No towers to rally' });
          return false;
        }
        for (const t of towers) {
          if (this.board !== 'painted' && t.lane !== lane) {
            t.lane = lane;
            if (!t.everSwitched) {
              t.everSwitched = true;
              p.stats.firstSwitchDelays.push(this.time - t.bornAt);
            }
          }
          this.musterFromTower(t);
        }
        this.telemetry({ t: this.time, type: 'commander_rally', side, lane, towers: towers.length });
        break;
      }
      case 'oda': {
        if (p.wildcardArmed) return false;
        p.wildcardArmed = true;
        break;
      }
      case 'brakk': {
        p.commanderActive = 8;
        break;
      }
      default:
        return false;
    }
    p.commanderCooldown = def.cooldown;
    p.stats.commanderUses++;
    this.events.push({ type: 'commander', side, id: p.commanderId, name: def.activeName });
    this.telemetry({ t: this.time, type: 'commander_use', side, commander: p.commanderId, pressure: this.lanePressure(side) });
    return true;
  }

  // -------------------------------------------------------------- multipliers

  private spend(p: PlayerState, amount: number): void {
    p.essence -= amount;
    p.stats.essenceSpent += amount;
  }

  private earn(p: PlayerState, amount: number): void {
    p.essence += amount;
    p.stats.essenceEarned += amount;
  }

  /** Out-of-game card level for this family, times the account-wide crit bonus. */
  private cardPower(side: Side, familyId: string): number {
    const load = this.config.loadout?.[side];
    if (!load) return 1;
    return unitPower(load.levels[familyId] ?? 1) * (1 + (load.crit || 0));
  }

  /** Stacked deck-boss auras for this side. */
  bossMods(side: Side): AuraMods {
    const cached = this.auraCache[side];
    if (cached) return cached;
    const mods = { minionHp: 1, minionDamage: 1, minionSpeed: 1, towerDamage: 1, fireRate: 1, spawnRate: 1, notes: [] as string[] };
    for (const id of this.players[side].bosses) {
      const aura = BOSS_BY_ID[id]?.aura;
      if (!aura) continue;
      if (aura.minionHp) mods.minionHp *= 1 + aura.minionHp;
      if (aura.minionDamage) mods.minionDamage *= 1 + aura.minionDamage;
      if (aura.minionSpeed) mods.minionSpeed *= 1 + aura.minionSpeed;
      if (aura.towerDamage) mods.towerDamage *= 1 + aura.towerDamage;
      if (aura.fireRate) mods.fireRate *= 1 + aura.fireRate;
      if (aura.spawnRate) mods.spawnRate *= 1 + aura.spawnRate;
      mods.notes.push(`${BOSS_BY_ID[id].name.replace(/^The /, '')}: ${aura.label}`);
    }
    this.auraCache[side] = mods;
    return mods;
  }

  /** Live numbers for a deck family, including shop, commander, card level, and boss auras. */
  unitPreview(side: Side, familyId: string): UnitPreview {
    const fam = FAMILY_BY_ID[familyId];
    const p = this.players[side];
    let rank = 1;
    for (const t of this.towersOf(side)) if (t.familyId === familyId) rank = Math.max(rank, t.rank);
    const power = this.cardPower(side, familyId);
    const dmg = fam.tower.damage * this.towerDamageMultiplier(side, rank, 0) * power;
    const fire = fam.tower.fireInterval / this.towerFireRateMultiplier(side, rank);
    const muster = this.spawnInterval(side, fam, rank, 0);
    const hp =
      fam.minion.hp *
      0.96 *
      (1 + this.rules.rankScaling.minionHp * (rank - 1)) *
      (1 + 0.15 * p.shop.muster) *
      (p.commanderId === 'vael' ? 1.15 : 1) *
      power *
      this.bossMods(side).minionHp;
    const md =
      fam.minion.damage *
      (1 + this.rules.rankScaling.minionDamage * (rank - 1)) *
      (1 + 0.1 * p.shop.muster) *
      (p.commanderId === 'brakk' ? (p.commanderActive > 0 ? 1.5 : 1.12) : 1) *
      power *
      this.bossMods(side).minionDamage;
    const speed = fam.minion.speed * this.minionSpeedMultiplier(side);
    const notes = [...this.bossMods(side).notes];
    if (p.shop.barrage) notes.push(`Barrage ${p.shop.barrage}: +${p.shop.barrage * 20}% tower damage`);
    if (p.shop.tempo) notes.push(`Tempo ${p.shop.tempo}: faster fire and muster`);
    if (p.shop.muster) notes.push(`Muster ${p.shop.muster}: +${p.shop.muster * 15}% minion health`);
    if (p.commanderId === 'vael') notes.push('Vael: +15% minion health');
    if (p.commanderId === 'brakk') notes.push(p.commanderActive > 0 ? 'Brakk rally: minions hit harder and move faster' : 'Brakk: +12% minion damage, +10% fire rate');
    if (power > 1.01) notes.push(`Card level ×${power.toFixed(2)}`);
    return {
      name: fam.name,
      role: fam.role,
      rank,
      attack: fam.tower.attack,
      damage: dmg,
      fire,
      muster,
      minionName: fam.minion.name,
      minionHp: Math.round(hp),
      minionDamage: md,
      minionSpeed: speed,
      minionMass: fam.minion.mass * (1 + 0.12 * (rank - 1)),
      about: fam.description,
      minionAbout: fam.minion.description,
      notes,
    };
  }

  towerDamageMultiplier(side: Side, rank: number, supportBonus: number): number {
    const p = this.players[side];
    return (1 + this.rules.rankScaling.towerDamage * (rank - 1)) * (1 + 0.2 * p.shop.barrage) * (1 + supportBonus) * this.bossMods(side).towerDamage;
  }

  towerFireRateMultiplier(side: Side, rank: number): number {
    const p = this.players[side];
    let m = (1 + this.rules.rankScaling.towerFireRate * (rank - 1)) * (1 + 0.1 * p.shop.tempo) * this.bossMods(side).fireRate;
    if (p.commanderId === 'brakk') m *= 1.1;
    if (p.commanderId === 'brakk' && p.commanderActive > 0) m *= 2;
    return m;
  }

  spawnInterval(side: Side, family: TowerFamilyDefinition, rank: number, supportBonus: number): number {
    const p = this.players[side];
    const rate = (1 + this.rules.rankScaling.spawnRate * (rank - 1)) * (1 + 0.12 * p.shop.tempo) * (1 + supportBonus * 0.5) * this.bossMods(side).spawnRate;
    return family.spawnInterval / (rate * 1.04);
  }

  minionSpeedMultiplier(side: Side): number {
    const p = this.players[side];
    let m = p.commanderId === 'brakk' && p.commanderActive > 0 ? 1.5 : 1;
    m *= this.bossMods(side).minionSpeed;
    if (this.overtime) m *= this.rules.overtimeSpeedMultiplier;
    return m * LANE_SPEED_SCALE * 0.95;
  }

  /** Gate cannons are active until Sudden Death breaches the walls. */
  get gatesOnline(): boolean {
    return !this.overtime;
  }

  // ---------------------------------------------------------------- minions

  private musterFromTower(t: TowerState): void {
    const family = FAMILY_BY_ID[t.familyId];
    const p = this.players[t.side];
    const def = family.minion;
    const power = this.cardPower(t.side, t.familyId);
    const mods = this.bossMods(t.side);
    const hpMult = 0.96 * (1 + this.rules.rankScaling.minionHp * (t.rank - 1)) * (1 + 0.15 * p.shop.muster) * (p.commanderId === 'vael' ? 1.15 : 1) * power * mods.minionHp;
    const dmgMult =
      (1 + this.rules.rankScaling.minionDamage * (t.rank - 1)) *
      (1 + 0.1 * p.shop.muster) *
      (p.commanderId === 'brakk' ? (p.commanderActive > 0 ? 1.5 : 1.12) : 1) *
      power *
      mods.minionDamage;
    const leak = def.special.leak ?? (t.rank >= this.rules.eliteRank ? this.rules.eliteLeak : 1);
    const m: MinionState = {
      id: this.nextId++,
      side: t.side,
      lane: t.lane,
      pos: def.special.deploy ?? 0,
      xOff: 0,
      fileCol: -1,
      familyId: family.id,
      faction: family.faction,
      rank: t.rank,
      kind: def.kind,
      hp: Math.round(def.hp * hpMult),
      maxHp: Math.round(def.hp * hpMult),
      damage: def.damage * dmgMult,
      attackInterval: def.attackInterval,
      range: def.range,
      speed: def.speed,
      mass: def.mass * (1 + 0.12 * (t.rank - 1)),
      attackTimer: def.attackInterval * 0.5,
      specialTimer: 0,
      slowTimer: 0,
      slowFactor: 1,
      weakTimer: 0,
      weakFactor: 1,
      opened: false,
      isBoss: false,
      unstoppable: false,
      leak,
      scale: def.scale * (1 + 0.08 * (t.rank - 1)),
      engaged: false,
      moving: true,
      pace: 0.35,
      shoved: false,
      age: 0,
      hitFlash: 0,
      special: { ...def.special },
      bodySprite: t.rank >= 3 ? 'body' : def.bodySprite,
      sourceId: t.id,
      hpBare: Math.round(def.hp * hpMult),
      damageBare: def.damage * dmgMult,
      spitBare: 0,
      blastBare: 0,
      bondHp: 0,
      bondDamage: 0,
      bondMarch: 0,
    };
    m.hp = m.hpBare;
    m.maxHp = m.hpBare;
    m.damage = m.damageBare;
    if (m.special.spitDamage) {
      m.special.spitDamage *= dmgMult;
      m.spitBare = m.special.spitDamage;
    }
    if (m.special.blastDamage) {
      m.special.blastDamage *= dmgMult;
      m.blastBare = m.special.blastDamage;
    }
    if (m.special.healAmount) m.special.healAmount *= 1 + 0.25 * (t.rank - 1);
    this.minions.push(m);
    this.mustered = true;
    this.writeAimPoint(m);
    p.stats.minionsMustered[t.lane]++;
    const c = tileCenter(t.side, t.tile);
    const e = minionXY(m);
    this.events.push({ type: 'muster', side: t.side, tile: t.tile, lane: t.lane, x: c.x, y: c.y, tx: e.x, ty: e.y });
  }

  private spawnBoss(def: BossDefinition, defender: Side, lane: Lane): void {
    const attacker: Side = defender === 0 ? 1 : 0;
    const m: MinionState = {
      id: this.nextId++,
      side: attacker,
      lane,
      pos: 0,
      xOff: 0,
      fileCol: -1,
      familyId: def.id,
      faction: def.faction,
      rank: 5,
      kind: 'boss',
      hp: def.hp,
      maxHp: def.hp,
      damage: def.damage,
      attackInterval: def.attackInterval,
      range: 30,
      speed: def.speed,
      mass: 3 * def.scale,
      attackTimer: 0.5,
      specialTimer: 0,
      slowTimer: 0,
      slowFactor: 1,
      weakTimer: 0,
      weakFactor: 1,
      opened: true,
      isBoss: true,
      bossName: def.name,
      unstoppable: def.unstoppable ?? false,
      leak: def.leak,
      scale: def.scale,
      engaged: false,
      moving: true,
      pace: 1,
      shoved: false,
      age: 0,
      hitFlash: 0,
      special: {},
      bodySprite: 'bodyHalf',
      sourceId: -1,
      hpBare: def.hp,
      damageBare: def.damage,
      spitBare: 0,
      blastBare: 0,
      bondHp: 0,
      bondDamage: 0,
      bondMarch: 0,
    };
    this.minions.push(m);
    const xy = minionXY(m);
    this.events.push({ type: 'bossSpawn', name: def.name, x: xy.x, y: xy.y });
    this.telemetry({ t: this.time, type: 'boss_spawn', boss: def.id, defender, lane });
  }

  // ------------------------------------------------------------------ update

  /** Advance the simulation by a fixed dt (seconds). */
  step(dt: number): void {
    if (this.ended) return;
    this.tick++;
    this.time += dt;

    if (!this.overtime && this.time >= this.rules.overtimeAt) {
      this.overtime = true;
      this.events.push({ type: 'overtime' });
      this.telemetry({ t: this.time, type: 'overtime', hearts: [this.players[0].hearts, this.players[1].hearts] });
    }

    this.updateEconomy(dt);
    this.updateBosses();
    this.refreshAimPoints();
    this.refreshElements();
    this.updateTowers(dt);
    if (this.mustered) {
      this.mustered = false;
      this.refreshElements();
    }
    this.updateGates(dt);
    this.updateMinions(dt);
    this.updateProjectiles(dt);
    this.updatePools(dt);
    this.minions = this.minions.filter((m) => m.hp > 0);
    this.checkEnd();
  }

  private updateEconomy(dt: number): void {
    for (const p of this.players) {
      const income =
        this.rules.baseIncome * (1 + 0.15 * p.shop.greed) * (this.overtime ? this.rules.overtimeIncomeMultiplier : 1) * p.incomeMultiplier;
      const posts = this.postCount(p.side) * this.rules.postPay * (1 + 0.15 * p.shop.greed) * p.incomeMultiplier;
      this.earn(p, (income + posts) * dt);
      if (p.commanderCooldown > 0) p.commanderCooldown = Math.max(0, p.commanderCooldown - dt);
      if (p.commanderActive > 0) p.commanderActive = Math.max(0, p.commanderActive - dt);
    }
  }

  private siegeWave = 0;

  private updateBosses(): void {
    // Sudden Death: siege engines keep coming until a fortress falls.
    if (this.overtime) {
      const s = this.rules.siege;
      const due = this.rules.overtimeAt + 5 + this.siegeWave * s.interval;
      if (this.time >= due) {
        this.siegeWave++;
        const lane: Lane = this.rng.chance(0.5) ? 0 : 1;
        // Engine health scales with the stronger defender's board so it stays a real threat late.
        const rankSum = Math.max(
          ...([0, 1] as Side[]).map((side) => this.towersOf(side).reduce((a, t) => a + t.rank, 0)),
          4,
        );
        const def: BossDefinition = {
          id: 'siege',
          name: `Siege Engine ${this.siegeWave}`,
          time: due,
          hp: Math.round((s.hp + s.hpGrowth * rankSum) * Math.pow(1.35, this.siegeWave - 1)),
          damage: s.damage,
          attackInterval: 1,
          speed: s.speed,
          leak: s.leak,
          scale: 1.6,
          laneRule: 'random',
          faction: 'gold',
          description: 'Sudden Death siege engine.',
          unstoppable: true,
        };
        for (const side of [0, 1] as Side[]) {
          this.events.push({ type: 'bossWarning', name: def.name, lanes: [lane], side, description: 'Siege engines will not stop until a fortress falls.' });
          this.spawnBoss(def, side, lane);
        }
      }
    }
    const due = (this.bossWave + 1) * this.bossEvery;
    if (due >= this.rules.overtimeAt) return;
    if (!this.bossWarned && this.time >= due - 3) {
      this.bossWarned = true;
      this.nextMarch = [this.pickMarch(this.players[0].bosses), this.pickMarch(this.players[1].bosses)];
      for (const side of [0, 1] as Side[]) {
        const incoming = this.nextMarch[1 - side];
        const name = BOSS_BY_ID[incoming.id]?.name ?? 'A boss';
        const laneName = incoming.lane === 0 ? 'left' : 'right';
        this.events.push({
          type: 'bossWarning',
          name,
          lanes: [incoming.lane],
          side,
          description: `Every minion on the field vanishes. One boss is entering the ${laneName} lane.`,
        });
      }
    }
    if (this.bossWarned && this.time >= due) {
      this.unleashBosses();
      this.bossWave++;
      this.bossWarned = false;
    }
  }

  /** One boss per side, down a random lane. Every ordinary minion bursts into that boss. */
  private unleashBosses(): void {
    const popped: [MinionState[], MinionState[]] = [[], []];
    for (const m of this.minions) {
      if (m.isBoss || m.hp <= 0) continue;
      popped[m.side].push(m);
      const xy = minionXY(m);
      this.events.push({ type: 'pop', x: xy.x, y: xy.y, rank: m.rank });
      m.hp = 0;
    }
    const expo = Math.exp(this.time / 150);
    for (const side of [0, 1] as Side[]) {
      const pick = this.nextMarch[side];
      const src = BOSS_BY_ID[pick.id];
      if (!src) continue;
      const value = popped[side].reduce((sum, m) => sum + Math.pow(m.rank, 1.35), 0);
      const def: BossDefinition = {
        ...src,
        hp: Math.round((260 + value * 18) * expo * (src.hpScale ?? 1)),
        damage: Math.round((18 + value * 1.1) * expo * (src.damageScale ?? 1)),
      };
      this.spawnBoss(def, (1 - side) as Side, pick.lane);
    }
    this.musterHoldUntil = this.time + 5;
  }

  private refreshElements(): void {
    this.elementLinks = [];
    this.indexSources();
    for (const p of this.players) {
      for (const t of p.towers) {
        if (!t) continue;
        t.bondDamage = 0;
        t.bondFire = 0;
      }
    }
    for (const m of this.minions) {
      m.bondHp = 0;
      m.bondDamage = 0;
      m.bondMarch = 0;
    }
    for (const side of [0, 1] as Side[]) {
      const p = this.players[side];
      const linked = new Set<string>();
      const towerHits = new Map<number, BondHit[]>();
      const minionHits = new Map<number, BondHit[]>();
      const pushTower = (t: TowerState, hit: BondHit) => {
        const list = towerHits.get(t.id) ?? [];
        list.push(hit);
        towerHits.set(t.id, list);
      };
      const pushMinion = (m: MinionState, hit: BondHit) => {
        const list = minionHits.get(m.id) ?? [];
        list.push(hit);
        minionHits.set(m.id, list);
      };
      for (const t of this.towersOf(side)) {
        const col = t.tile % 5;
        const row = Math.floor(t.tile / 5);
        const neighbors = [col < 4 ? t.tile + 1 : -1, row < 2 ? t.tile + 5 : -1];
        for (const n of neighbors) {
          if (n < 0) continue;
          const other = p.towers[n];
          if (!other) continue;
          const ea = ELEMENT_OF[t.familyId];
          const eb = ELEMENT_OF[other.familyId];
          if (!ea || !eb) continue;
          const effect = bondEffect(ea, eb);
          const meet = this.minionsMeet(t.id, other.id);
          const hit = { effect, meet, tileA: t.tile, tileB: other.tile };
          pushTower(t, hit);
          pushTower(other, hit);
          for (const m of this.minions) {
            if (m.sourceId === t.id || m.sourceId === other.id) pushMinion(m, hit);
          }
          linked.add(this.pairKey(t.id, other.id));
          this.elementLinks.push({
            side,
            tileA: t.tile,
            tileB: other.tile,
            tier: effect.tier,
            color: bondColor(effect.tier, ea),
            doubled: meet,
            form: 'none',
          });
        }
      }
      const mine = this.minions.filter((m) => m.side === side && !m.isBoss && m.sourceId >= 0);
      for (let i = 0; i < mine.length; i++) {
        for (let j = i + 1; j < mine.length; j++) {
          const a = mine[i];
          const b = mine[j];
          if (a.lane !== b.lane || Math.abs(a.pos - b.pos) > 46) continue;
          if (a.sourceId !== b.sourceId && linked.has(this.pairKey(a.sourceId, b.sourceId))) continue;
          const ea = ELEMENT_OF[a.familyId];
          const eb = ELEMENT_OF[b.familyId];
          if (!ea || !eb) continue;
          const hit = { effect: bondEffect(ea, eb), meet: false, tileA: -1, tileB: -1 };
          pushMinion(a, hit);
          pushMinion(b, hit);
        }
      }
      const bracketBonds = new Set<string>();
      const concordTiles = new Set<number>();
      const concordBonds = new Set<string>();
      const elementAt = (tile: number): string | null => {
        const tower = p.towers[tile];
        return tower ? (ELEMENT_OF[tower.familyId] ?? null) : null;
      };
      const scanLine = (tiles: number[]) => {
        let run: number[] = [];
        const flush = () => {
          if (run.length >= 3) {
            for (let i = 0; i <= run.length - 3; i++) {
              const a = elementAt(run[i]);
              const b = elementAt(run[i + 1]);
              const c = elementAt(run[i + 2]);
              if (a && b && c && a === c && a !== b) {
                bracketBonds.add(this.pairKey(run[i], run[i + 1]));
                bracketBonds.add(this.pairKey(run[i + 1], run[i + 2]));
              }
            }
          }
          if (run.length >= 4) {
            const kinds = new Set(run.map((tile) => elementAt(tile)));
            if (kinds.has('fire') && kinds.has('water') && kinds.has('earth') && kinds.has('wind')) {
              for (const tile of run) concordTiles.add(tile);
              for (let i = 0; i < run.length - 1; i++) concordBonds.add(this.pairKey(run[i], run[i + 1]));
            }
          }
          run = [];
        };
        for (const tile of tiles) {
          if (p.towers[tile]) run.push(tile);
          else flush();
        }
        flush();
      };
      for (let row = 0; row < 3; row++) scanLine([0, 1, 2, 3, 4].map((col) => row * 5 + col));
      for (let col = 0; col < 5; col++) scanLine([0, 1, 2].map((row) => row * 5 + col));
      const formPower = (tileA: number, tileB: number) => {
        if (tileA < 0) return 1;
        const key = this.pairKey(tileA, tileB);
        let power = 1;
        if (bracketBonds.has(key)) power *= 1.5;
        if (concordBonds.has(key)) power *= 1.5;
        return power;
      };
      const lift = (effect: BondEffect, power: number): BondEffect => ({
        tier: effect.tier,
        turretDamage: effect.turretDamage > 0 ? effect.turretDamage * power : effect.turretDamage,
        turretFire: effect.turretFire > 0 ? effect.turretFire * power : effect.turretFire,
        minionDamage: effect.minionDamage > 0 ? effect.minionDamage * power : effect.minionDamage,
        minionHp: effect.minionHp > 0 ? effect.minionHp * power : effect.minionHp,
        march: effect.march > 0 ? effect.march * power : effect.march,
      });
      for (const link of this.elementLinks) {
        if (link.side !== side) continue;
        const key = this.pairKey(link.tileA, link.tileB);
        const bracket = bracketBonds.has(key);
        const concord = concordBonds.has(key);
        link.form = bracket && concord ? 'both' : bracket ? 'bracket' : concord ? 'concord' : 'none';
      }
      for (const t of this.towersOf(side)) {
        const hits = towerHits.get(t.id);
        if (hits) {
          let used = 0;
          for (const hit of hits) {
            if (used === 4) break;
            used++;
            this.addTowerBond(t, lift(hit.effect, formPower(hit.tileA, hit.tileB)), hit.meet ? 2 : 1);
          }
        }
        if (concordTiles.has(t.tile)) this.addTowerBond(t, CONCORD_GIFT, 1);
      }
      for (const m of mine) {
        const hits = minionHits.get(m.id);
        if (hits) {
          let used = 0;
          for (const hit of hits) {
            if (used === 6) break;
            used++;
            this.addMinionBond(m, lift(hit.effect, formPower(hit.tileA, hit.tileB)), hit.meet ? 2 : 1);
          }
        }
        if (m.sourceId >= 0 && concordTiles.has(this.towerTile(side, m.sourceId))) this.addMinionBond(m, CONCORD_GIFT, 1);
      }
    }
    for (const m of this.minions) {
      if (m.isBoss) {
        m.bondMarch = 1;
        continue;
      }
      const ratio = m.maxHp > 0 ? Math.min(1, m.hp / m.maxHp) : 1;
      const hp = clampBond(m.bondHp);
      m.maxHp = Math.max(1, Math.round(m.hpBare * hp));
      m.hp = Math.max(0, m.maxHp * ratio);
      const inspire = this.inspireBonus(m);
      m.damage = m.damageBare * clampBond(m.bondDamage) * inspire;
      if (m.spitBare) m.special.spitDamage = m.spitBare * clampBond(m.bondDamage) * inspire;
      if (m.blastBare) m.special.blastDamage = m.blastBare * clampBond(m.bondDamage) * inspire;
      m.bondMarch = clampBond(m.bondMarch);
    }
  }

  /** Hits land softer while a curse is on this minion. */
  private outputPower(m: MinionState): number {
    return m.weakTimer > 0 ? m.weakFactor : 1;
  }

  /** Opening blow for ambushers, then the curse, if any. */
  private strikeDamage(m: MinionState): number {
    let damage = m.damage * this.outputPower(m);
    const ambush = m.special.ambush ?? 0;
    if (!m.opened && ambush > 1) {
      damage *= ambush;
      m.opened = true;
    }
    return damage;
  }

  /** The strongest Standard standing near this minion. Does not include itself. */
  private inspireBonus(m: MinionState): number {
    let best = 1;
    for (const other of this.minions) {
      if (other === m || other.hp <= 0 || other.side !== m.side || other.lane !== m.lane) continue;
      const bonus = other.special.inspire ?? 0;
      if (bonus > 0 && Math.abs(other.pos - m.pos) <= 80) best = Math.max(best, 1 + bonus);
    }
    return best;
  }

  private addTowerBond(t: TowerState, effect: ReturnType<typeof bondEffect>, mult: number): void {
    t.bondDamage += effect.turretDamage * mult;
    t.bondFire += effect.turretFire * mult;
  }

  private addMinionBond(m: MinionState, effect: ReturnType<typeof bondEffect>, mult: number): void {
    m.bondHp += effect.minionHp * mult;
    m.bondDamage += effect.minionDamage * mult;
    m.bondMarch += effect.march * mult;
  }

  private indexSources(): void {
    this.sourceGroups.clear();
    for (const m of this.minions) {
      if (m.hp <= 0 || m.isBoss || m.sourceId < 0) continue;
      const list = this.sourceGroups.get(m.sourceId);
      if (list) list.push(m);
      else this.sourceGroups.set(m.sourceId, [m]);
    }
  }

  private refreshAimPoints(): void {
    if (this.aimTick === this.tick) return;
    this.aimTick = this.tick;
    for (const m of this.minions) if (m.hp > 0) this.writeAimPoint(m);
  }

  private writeAimPoint(m: MinionState): void {
    let p = this.aimPoints.get(m.id);
    if (!p) {
      p = { x: 0, y: 0 };
      this.aimPoints.set(m.id, p);
    }
    const along = m.side === 0 ? m.pos : LANE_LENGTH - m.pos;
    const s = laneSample(m.lane, along);
    p.x = s.x + s.nx * m.xOff;
    p.y = s.y + s.ny * m.xOff;
  }

  private minionsMeet(towerA: number, towerB: number): boolean {
    const fromA = this.sourceGroups.get(towerA);
    const fromB = this.sourceGroups.get(towerB);
    if (!fromA || !fromB) return false;
    for (const a of fromA) {
      for (const b of fromB) {
        if (a.lane === b.lane && Math.abs(a.pos - b.pos) <= 46) return true;
      }
    }
    return false;
  }

  private towerTile(side: Side, towerId: number): number {
    for (const t of this.towersOf(side)) if (t.id === towerId) return t.tile;
    return -1;
  }

  private pairKey(a: number, b: number): string {
    return a < b ? `${a}:${b}` : `${b}:${a}`;
  }

  private updateTowers(dt: number): void {
    const lanePop: [[number, number], [number, number]] = [
      [0, 0],
      [0, 0],
    ];
    for (const m of this.minions) if (m.hp > 0) lanePop[m.side][m.lane]++;
    for (const p of this.players) {
      // Support auras first.
      for (const t of p.towers) if (t) t.supportBonus = 0;
      for (const t of p.towers) {
        if (!t) continue;
        const fam = FAMILY_BY_ID[t.familyId];
        if (fam.tower.attack !== 'support') continue;
        const bonus = (fam.tower.support ?? 0) * t.rank * this.cardPower(t.side, t.familyId);
        const col = t.tile % 5;
        const row = Math.floor(t.tile / 5);
        const neighbors = [
          col > 0 ? t.tile - 1 : -1,
          col < 4 ? t.tile + 1 : -1,
          row > 0 ? t.tile - 5 : -1,
          row < 2 ? t.tile + 5 : -1,
        ];
        for (const n of neighbors) {
          if (n < 0) continue;
          const other = p.towers[n];
          if (other && FAMILY_BY_ID[other.familyId].tower.attack !== 'support') other.supportBonus += bonus;
        }
      }

      for (const t of p.towers) {
        if (!t) continue;
        const fam = FAMILY_BY_ID[t.familyId];
        t.pulse += dt;
        t.sinceShot += dt;

        // Muster (holds when the lane is at capacity; resumes as soon as there is room)
        if (this.time > 2.5 && this.time >= this.musterHoldUntil) {
          t.spawnTimer -= dt;
          if (t.spawnTimer <= 0) {
            if (lanePop[t.side][t.lane] < this.rules.laneCap) {
              this.musterFromTower(t);
              lanePop[t.side][t.lane]++;
              t.spawnTimer = this.spawnInterval(t.side, fam, t.rank, t.supportBonus);
            } else t.spawnTimer = 0.35;
          }
        }

        // Fire
        if (fam.tower.attack === 'support') continue;
        const c = tileCenter(t.side, t.tile);
        const target = this.findTowerTarget(t, fam, c);
        t.targetId = target ? target.id : -1;
        if (target) {
          const xy = this.aimPoints.get(target.id) ?? minionXY(target);
          const want = Math.atan2(xy.y - c.y, xy.x - c.x);
          let turn = want - t.angle;
          while (turn > Math.PI) turn -= Math.PI * 2;
          while (turn < -Math.PI) turn += Math.PI * 2;
          const step = 6 * dt;
          t.angle += Math.max(-step, Math.min(step, turn));
        }
        t.fireTimer -= dt * this.towerFireRateMultiplier(t.side, t.rank) * clampBond(t.bondFire);
        if (t.fireTimer <= 0 && target) {
          t.fireTimer = fam.tower.fireInterval;
          t.sinceShot = 0;
          const damage = fam.tower.damage * this.towerDamageMultiplier(t.side, t.rank, t.supportBonus) * this.cardPower(t.side, t.familyId) * clampBond(t.bondDamage);
          this.towerFire(t, fam, c, target, damage);
        }
      }
    }
  }

  /** Per side & lane: seconds until the gate cannon fires again. */
  readonly gateTimers: [[number, number], [number, number]] = [
    [0, 0],
    [0, 0],
  ];
  /** Visual: seconds since each gate fired. */
  readonly gateSinceShot: [[number, number], [number, number]] = [
    [9, 9],
    [9, 9],
  ];

  private updateGates(dt: number): void {
    for (const side of [0, 1] as Side[]) {
      for (const lane of [0, 1] as Lane[]) {
        this.gateSinceShot[side][lane] += dt;
        if (!this.gatesOnline) continue;
        this.gateTimers[side][lane] -= dt;
        if (this.gateTimers[side][lane] > 0) continue;
        // Enemy minion in this lane closest to our gate (highest pos) within range.
        let best: MinionState | null = null;
        for (const m of this.minions) {
          if (m.side === side || m.lane !== lane || m.hp <= 0) continue;
          const remaining = LANE_LENGTH - m.pos;
          if (remaining > this.rules.gate.range) continue;
          if (!best || m.pos > best.pos) best = m;
        }
        if (!best) continue;
        this.gateTimers[side][lane] = this.rules.gate.interval;
        this.gateSinceShot[side][lane] = 0;
        const from = laneEntry(side, lane);
        const xy = minionXY(best);
        this.projectiles.push(this.makeProjectile(side, 'gate', from, best, xy.x, xy.y, 460, this.rules.gate.damage, 0, 0, 0, 0));
        this.events.push({ type: 'shot', side, x: from.x, y: from.y, kind: 'gate' });
      }
    }
  }

  private findTowerTarget(t: TowerState, fam: TowerFamilyDefinition, c: { x: number; y: number }): MinionState | null {
    const range = fam.tower.range;
    let best: MinionState | null = null;
    let bestScore = -Infinity;
    const candidates: MinionState[] = [];
    for (const m of this.minions) {
      if (m.side === t.side || m.hp <= 0) continue;
      const xy = this.aimPoints.get(m.id);
      if (!xy) continue;
      const d = Math.hypot(xy.x - c.x, xy.y - c.y);
      if (d > range) continue;
      if (fam.tower.attack === 'mortar') {
        candidates.push(m);
        continue;
      }
      // Prefer the minion closest to our fortress; bosses get a bonus.
      const score = m.pos + (m.isBoss ? 60 : 0);
      if (score > bestScore) {
        bestScore = score;
        best = m;
      }
    }
    if (fam.tower.attack === 'mortar' && candidates.length > 0) {
      // Keep aiming at the same target while it lives; otherwise pick randomly.
      const same = candidates.find((m) => m.id === t.targetId);
      return same ?? this.rng.pick(candidates);
    }
    return best;
  }

  private towerFire(t: TowerState, fam: TowerFamilyDefinition, c: { x: number; y: number }, target: MinionState, damage: number): void {
    const from = { x: c.x + Math.cos(t.angle) * 22, y: c.y + Math.sin(t.angle) * 22 };
    switch (fam.tower.attack) {
      case 'laser': {
        this.applyDamage(target, damage, t.side, 'laser');
        break;
      }
      case 'mortar': {
        const xy = minionXY(target);
        const along = target.side === 0 ? target.pos : LANE_LENGTH - target.pos;
        const dir = laneSample(target.lane, along);
        const lead = target.moving ? target.speed * this.minionSpeedMultiplier(target.side) * 0.35 : 0;
        const sign = target.side === 0 ? 1 : -1;
        this.projectiles.push(
          this.makeProjectile(
            t.side,
            'mortar',
            from,
            target,
            xy.x + dir.tx * lead * sign,
            xy.y + dir.ty * lead * sign,
            0,
            damage,
            fam.tower.splash ?? 40,
            0,
            0,
            1.15,
          ),
        );
        this.events.push({ type: 'shot', side: t.side, x: from.x, y: from.y, kind: 'mortar' });
        break;
      }
      default: {
        const kind: ProjectileKind = fam.tower.attack === 'fireball' ? 'fireball' : fam.tower.attack === 'frost' ? 'frost' : 'bolt';
        const speed = kind === 'fireball' ? 260 : kind === 'frost' ? 320 : 420;
        const xy = minionXY(target);
        this.projectiles.push(
          this.makeProjectile(
            t.side,
            kind,
            from,
            target,
            xy.x,
            xy.y,
            speed,
            damage,
            fam.tower.splash ?? 0,
            fam.tower.slow ?? 0,
            fam.tower.slowDuration ?? 0,
            0,
          ),
        );
        this.events.push({ type: 'shot', side: t.side, x: from.x, y: from.y, kind });
      }
    }
  }

  private makeProjectile(
    side: Side,
    kind: ProjectileKind,
    from: { x: number; y: number },
    target: MinionState | null,
    tx: number,
    ty: number,
    speed: number,
    damage: number,
    splash: number,
    slow: number,
    slowDuration: number,
    duration: number,
  ): ProjectileState {
    return {
      id: this.nextId++,
      side,
      kind,
      x: from.x,
      y: from.y,
      sx: from.x,
      sy: from.y,
      targetId: target ? target.id : -1,
      tx,
      ty,
      lane: target ? target.lane : 0,
      speed,
      damage,
      splash,
      slow,
      slowDuration,
      t: 0,
      duration,
      pierceChance: 0,
      weaken: 0,
      weakenDuration: 0,
      pierced: false,
      rotation: Math.atan2(ty - from.y, tx - from.x),
    };
  }

  private updateMinions(dt: number): void {
    const bySideLane: MinionState[][] = [[], [], [], []];
    for (const m of this.minions) {
      if (m.hp <= 0) continue;
      bySideLane[m.side * 2 + m.lane].push(m);
    }
    // Sort each group by progress (leader first).
    for (const g of bySideLane) g.sort((a, b) => b.pos - a.pos);

    // Up to three abreast. Centers stay inside the 44px road: a sprite is about
    // 28px wide, so ±14 put the outer bodies on the grass. Ranged and melee keep
    // separate stopping lines so a shooter does not cork the troops behind him.
    const FILE = 3;
    const ROW_GAP = 36;
    const FILE_LIMIT = 9;
    const COLS = [-FILE_LIMIT, 0, FILE_LIMIT];
    const COL_ORDER = [1, 0, 2];
    const slots = new Map<number, { row: number; col: number; stop: number; x: number; ahead: MinionState | null }>();
    for (const group of bySideLane) {
      if (!group.length) continue;
      const foes = bySideLane[(1 - group[0].side) * 2 + group[0].lane];
      let foe: MinionState | null = null;
      for (const e of foes) if (!foe || e.pos > foe.pos) foe = e;
      const bands = new Map<number, MinionState[]>();
      for (const m of group) {
        if (m.isBoss || m.unstoppable) continue;
        const band = m.range >= 50 ? 1 : 0;
        const list = bands.get(band) ?? [];
        list.push(m);
        bands.set(band, list);
      }
      for (const list of bands.values()) {
        list.sort((a, b) => {
          const dp = b.pos - a.pos;
          if (Math.abs(dp) < 16) return a.id - b.id;
          return dp || a.id - b.id;
        });
        const hold = list[0].range + 12 + (foe ? 8 * (foe.scale - 1) : 0);
        const frontStop = foe ? LANE_LENGTH - foe.pos - hold : LANE_LENGTH + 40;
        for (let rowStart = 0; rowStart < list.length; rowStart += FILE) {
          const rowUnits = list.slice(rowStart, rowStart + FILE);
          const row = Math.floor(rowStart / FILE);
          const used = new Set<number>();
          const prefer = rowUnits.length === 1 ? [1] : rowUnits.length === 2 ? [1, 0] : COL_ORDER;
          rowUnits.forEach((m, n) => {
            const i = rowStart + n;
            let col = m.fileCol;
            if (rowUnits.length < FILE || col < 0 || col > 2 || used.has(col)) {
              col = prefer.find((c) => !used.has(c)) ?? 1;
            }
            used.add(col);
            m.fileCol = col;
            const ahead = row > 0 ? list[i - FILE] : null;
            const stop = ahead ? Math.min(frontStop, ahead.pos) - ROW_GAP : frontStop;
            slots.set(m.id, { row, col, stop, x: COLS[col], ahead });
          });
        }
      }
      // Two bands can claim the same column while abreast. Slide the rear body
      // into a free column instead of shoving it off the road.
      const seated = group.filter((m) => slots.has(m.id)).sort((a, b) => b.pos - a.pos || a.id - b.id);
      for (let i = 0; i < seated.length; i++) {
        const a = seated[i];
        const sa = slots.get(a.id)!;
        for (let j = i + 1; j < seated.length; j++) {
          const b = seated[j];
          if (a.pos - b.pos > 22) break;
          const sb = slots.get(b.id)!;
          if (sa.col !== sb.col) continue;
          const used = new Set<number>();
          for (const o of seated) {
            if (o.id === b.id || Math.abs(o.pos - b.pos) > 22) continue;
            used.add(slots.get(o.id)!.col);
          }
          const next = COL_ORDER.find((c) => !used.has(c));
          if (next === undefined) continue;
          sb.col = next;
          sb.x = COLS[next];
          b.fileCol = next;
        }
      }
    }

    for (const m of this.minions) {
      if (m.hp <= 0) continue;
      m.age += dt;
      if (m.hitFlash > 0) m.hitFlash -= dt;
      if (m.slowTimer > 0) {
        m.slowTimer -= dt;
        if (m.slowTimer <= 0) m.slowFactor = 1;
      }
      if (m.weakTimer > 0) {
        m.weakTimer -= dt;
        if (m.weakTimer <= 0) m.weakFactor = 1;
      }
      m.specialTimer -= dt;

      const enemies = bySideLane[(1 - m.side) * 2 + m.lane];
      const friends = bySideLane[m.side * 2 + m.lane];
      let swing = 1;
      if (!m.isBoss) {
        for (const e of enemies) {
          const drag = e.special.anchor ?? 0;
          if (e.hp > 0 && drag > 0 && Math.abs(LANE_LENGTH - m.pos - e.pos) < 42) swing = Math.min(swing, 1 - Math.min(0.55, drag));
        }
      }
      m.attackTimer -= dt * swing;

      const nearest = enemies[0] ?? null;
      const nearestGap = nearest ? LANE_LENGTH - m.pos - nearest.pos : Infinity;

      this.minionSpecials(m, nearest, nearestGap, friends, enemies);
      if (m.hp <= 0) continue;

      const speed = m.speed * m.bondMarch * m.slowFactor * this.minionSpeedMultiplier(m.side);

      if (m.isBoss || m.unstoppable) {
        const engageRange = m.range + (nearest ? 10 * (nearest.scale - 1) + 10 * (m.scale - 1) : 0);
        m.engaged = nearest !== null && nearestGap <= engageRange;
        if (m.unstoppable) {
          if (m.engaged && nearest && m.attackTimer <= 0) {
            m.attackTimer = m.attackInterval;
            for (const e of enemies) {
              const g = LANE_LENGTH - m.pos - e.pos;
              if (g <= engageRange + 20) this.applyDamage(e, m.damage, m.side, 'melee');
            }
          }
          m.moving = true;
          m.pos += speed * 0.5 * dt;
          if (m.pos >= LANE_LENGTH) this.leak(m);
          continue;
        }
        if (m.engaged && nearest) {
          if (m.attackTimer <= 0 && m.damage > 0) {
            m.attackTimer = m.attackInterval;
            this.applyDamage(nearest, m.damage, m.side, 'melee');
          }
          m.moving = false;
          continue;
        }
        let blocked = false;
        if (nearest && nearestGap < 16) blocked = true;
        if (blocked) {
          m.moving = false;
          continue;
        }
        m.moving = true;
        m.pos += speed * dt;
        if (m.pos >= LANE_LENGTH) this.leak(m);
        continue;
      }

      const slot = slots.get(m.id);
      if (!slot) continue;
      let want = slot.stop;
      for (const f of friends) {
        if (f === m || (!f.isBoss && !f.unstoppable)) continue;
        if (f.pos > m.pos) want = Math.min(want, f.pos - 30 * f.scale);
      }
      if (nearest) {
        const minGap = 14 + 8 * (nearest.scale - 1);
        want = Math.min(want, LANE_LENGTH - nearest.pos - minGap);
      }
      const inContact = Boolean(nearest && slot.row === 0 && nearestGap <= 40 && nearestGap <= m.range + 18);
      const closing = !inContact && m.pos < want - 1;
      m.pace += ((closing ? 1 : 0) - m.pace) * Math.min(1, dt * 2.2);
      if (closing) {
        m.moving = m.pace > 0.2;
        m.engaged = false;
        m.pos = Math.min(want, m.pos + speed * m.pace * dt);
      } else {
        m.moving = false;
        m.engaged = nearest !== null && slot.row === 0 && nearestGap <= m.range + 20;
      }
      if (slot.ahead && m.pos > slot.ahead.pos - ROW_GAP) m.pos = slot.ahead.pos - ROW_GAP;
      const lateral = Math.max(-FILE_LIMIT, Math.min(FILE_LIMIT, slot.x));
      m.xOff += (lateral - m.xOff) * Math.min(1, dt * 10);

      if (m.engaged && nearest) {
        const front = [...enemies].sort((a, b) => b.pos - a.pos || a.id - b.id).slice(0, FILE);
        let foe = front[0] ?? nearest;
        for (const e of front) {
          if (Math.abs(e.xOff - m.xOff) < Math.abs(foe.xOff - m.xOff)) foe = e;
        }
        if (m.kind === 'sapper') {
          this.detonate(m, enemies);
          continue;
        }
        if (m.attackTimer <= 0 && m.damage > 0) {
          m.attackTimer = m.attackInterval;
          const strike = this.strikeDamage(m);
          if (m.kind === 'ranged') {
            const from = minionXY(m);
            const xy = minionXY(foe);
            const p = this.makeProjectile(m.side, 'arrow', from, foe, xy.x, xy.y, 380, strike, 0, m.special.curseSlow ?? 0, m.special.curseDuration ?? 0, 0);
            p.pierceChance = m.special.pierceChance ?? 0;
            p.weaken = m.special.curseDamage ?? 0;
            p.weakenDuration = m.special.curseDuration ?? 0;
            this.projectiles.push(p);
            this.events.push({ type: 'shot', side: m.side, x: from.x, y: from.y, kind: 'arrow' });
          } else {
            this.applyDamage(foe, strike, m.side, 'melee');
          }
        }
      }
      if (m.pos >= LANE_LENGTH) this.leak(m);
    }
    this.pressLanes(dt, bySideLane);
    for (const m of this.minions) {
      if (m.hp <= 0) continue;
      const slot = slots.get(m.id);
      if (!slot?.ahead) continue;
      const limit = slot.ahead.pos - ROW_GAP;
      if (m.pos > limit) {
        m.pos = limit;
        m.pace = Math.min(m.pace, 0.3);
      }
    }
  }

  /**
   * Mass of the connected column behind the front. A gap of about one rank breaks
   * the chain, so reinforcements still walking up do not push. Melee in contact
   * own the front; archers only start the chain when no melee has reached them.
   * Unstoppable bodies never anchor it.
   */
  private pressChain(list: MinionState[]): MinionState[] {
    const living = list.filter((m) => m.hp > 0 && !m.unstoppable);
    if (!living.length) return [];
    living.sort((a, b) => b.pos - a.pos || a.id - b.id);
    const lead = living[0].pos;
    const meleeUp = living.some((m) => (m.isBoss || m.range < 50) && lead - m.pos <= 26);
    const chain: MinionState[] = [];
    let cursor = -1;
    for (const m of living) {
      const melee = m.isBoss || m.range < 50;
      if (meleeUp && !melee) continue;
      if (cursor < 0) {
        if (lead - m.pos > 26) break;
        chain.push(m);
        cursor = m.pos;
        continue;
      }
      if (cursor - m.pos > 44) break;
      chain.push(m);
      cursor = m.pos;
    }
    return chain;
  }

  /**
   * Once both fronts are touching, the line slides toward the lighter side.
   * The shove uses the whole connected chain. Equal mass holds. The chain slides together.
   */
  private pressLanes(dt: number, bySideLane: MinionState[][]): void {
    const press = 46;
    for (let lane = 0; lane < 2; lane++) {
      const fronts = [this.pressChain(bySideLane[lane]), this.pressChain(bySideLane[2 + lane])];
      if (!fronts[0].length || !fronts[1].length) continue;
      const masses = fronts.map((group) => group.reduce((sum, m) => sum + m.mass, 0));
      const lead0 = Math.max(...fronts[0].map((m) => m.pos));
      const lead1 = Math.max(...fronts[1].map((m) => m.pos));
      if (LANE_LENGTH - lead0 - lead1 > 40) continue;
      const total = masses[0] + masses[1];
      if (total <= 0) continue;
      const delta = ((masses[0] - masses[1]) / total) * press * dt;
      const slide = (group: MinionState[], d: number) => {
        if (Math.abs(d) < 0.0001) return;
        for (const m of group) m.pos = Math.max(0, m.pos + d);
      };
      slide(fronts[0], delta);
      slide(fronts[1], -delta);
      const gap = LANE_LENGTH - Math.max(...fronts[0].map((m) => m.pos)) - Math.max(...fronts[1].map((m) => m.pos));
      if (gap < 14) {
        const overlap = Math.min(14 - gap, 28 * dt);
        slide(fronts[0], -overlap * (masses[1] / total));
        slide(fronts[1], -overlap * (masses[0] / total));
      }
    }
  }

  private minionSpecials(m: MinionState, nearest: MinionState | null, gap: number, friends: MinionState[], enemies: MinionState[]): void {
    switch (m.kind) {
      case 'firespitter': {
        if (nearest && gap <= (m.special.spitRange ?? 90) && m.specialTimer <= 0) {
          m.specialTimer = m.special.spitCooldown ?? 2.5;
          const from = minionXY(m);
          const xy = minionXY(nearest);
          this.projectiles.push(
            this.makeProjectile(m.side, 'fire', from, nearest, xy.x, xy.y, 240, (m.special.spitDamage ?? 20) * this.outputPower(m), m.special.spitSplash ?? 30, 0, 0, 0),
          );
          this.events.push({ type: 'shot', side: m.side, x: from.x, y: from.y, kind: 'fire' });
        }
        break;
      }
      case 'healer': {
        if (m.specialTimer <= 0) {
          let best: MinionState | null = null;
          let bestMissing = 0;
          for (const f of friends) {
            if (f === m) continue;
            if (Math.abs(f.pos - m.pos) > (m.special.healRange ?? 70)) continue;
            const missing = f.maxHp - f.hp;
            if (missing > bestMissing) {
              bestMissing = missing;
              best = f;
            }
          }
          if (!best && m.hp < m.maxHp) best = m;
          if (best) {
            m.specialTimer = m.special.healCooldown ?? 1.4;
            const amount = Math.min(best.maxHp - best.hp, m.special.healAmount ?? 8);
            best.hp += amount;
            const xy = minionXY(best);
            this.events.push({ type: 'heal', x: xy.x, y: xy.y, amount });
          }
        }
        break;
      }
      case 'tank': {
        if (!m.shoved && m.hp < m.maxHp * (m.special.shoveThreshold ?? 0.5)) {
          m.shoved = true;
          const xy = minionXY(m);
          this.events.push({ type: 'shove', x: xy.x, y: xy.y });
          for (const e of enemies) {
            const g = LANE_LENGTH - m.pos - e.pos;
            if (g < 60 && !e.isBoss) {
              e.pos = Math.max(0, e.pos - (m.special.shoveDistance ?? 40));
              this.applyDamage(e, m.damage * this.outputPower(m), m.side, 'melee');
            }
          }
        }
        break;
      }
      default:
        break;
    }
  }

  private detonate(m: MinionState, enemies: MinionState[]): void {
    const xy = minionXY(m);
    const radius = m.special.blastRadius ?? 40;
    this.events.push({ type: 'explosion', x: xy.x, y: xy.y, radius, kind: 'blast' });
    for (const e of enemies) {
      const g = Math.abs(LANE_LENGTH - m.pos - e.pos);
      if (g <= radius) this.applyDamage(e, (m.special.blastDamage ?? 40) * this.outputPower(m), m.side, 'melee');
    }
    m.hp = 0;
    this.events.push({ type: 'death', x: xy.x, y: xy.y, side: m.side, faction: m.faction, isBoss: false, scale: m.scale });
  }

  private leak(m: MinionState): void {
    const victim: Side = m.side === 0 ? 1 : 0;
    const amount = m.leak * (this.overtime ? this.rules.overtimeLeakMultiplier : 1);
    const v = this.players[victim];
    const a = this.players[m.side];
    v.hearts = Math.max(0, v.hearts - amount);
    v.stats.leaksTaken[m.lane] += amount;
    a.stats.leaksDealt[m.lane] += amount;
    m.hp = 0;
    const xy = minionXY(m);
    this.events.push({ type: 'leak', side: victim, lane: m.lane, amount, x: xy.x, y: xy.y });
    this.telemetry({
      t: this.time,
      type: 'leak',
      side: victim,
      lane: m.lane,
      amount,
      source: m.isBoss ? 'boss' : m.familyId,
      rank: m.rank,
      heartsLeft: v.hearts,
    });
  }

  applyDamage(target: MinionState, amount: number, from: Side, kind: ProjectileKind | 'melee'): void {
    if (target.hp <= 0) return;
    if (!target.isBoss && target.engaged && (target.special.guard ?? 0) > 0) amount *= 1 - Math.min(0.4, target.special.guard);
    const xy = minionXY(target);
    if (target.kind === 'tank' && this.rng.chance(target.special.ignoreChance ?? 0)) {
      this.events.push({ type: 'ignore', x: xy.x, y: xy.y });
      return;
    }
    const dealt = Math.min(target.hp, amount);
    target.hp -= amount;
    target.hitFlash = 0.12;
    const p = this.players[from];
    p.stats.damageDealt += dealt;
    if (target.isBoss) {
      p.stats.bossDamage += dealt;
      this.earn(p, dealt * this.rules.bossDamageReward);
    }
    this.events.push({ type: 'hit', x: xy.x, y: xy.y, kind, damage: dealt, side: target.side });
    if (target.hp <= 0) this.kill(target, from);
  }

  private kill(m: MinionState, by: Side): void {
    const p = this.players[by];
    p.stats.kills++;
    const xy = minionXY(m);
    let reward = this.rules.killReward(m.rank);
    reward *= this.wavePay();
    reward *= 1 + 0.15 * p.shop.greed;
    if (m.isBoss) {
      p.stats.bossKills++;
      reward = this.rules.bossKillReward * (1 + 0.15 * p.shop.greed);
      this.telemetry({ t: this.time, type: 'boss_kill', side: by, lane: m.lane });
    }
    reward = Math.round(reward);
    this.earn(p, reward);
    this.events.push({ type: 'death', x: xy.x, y: xy.y, side: m.side, faction: m.faction, isBoss: m.isBoss, scale: m.scale });
    this.events.push({ type: 'essence', side: by, amount: reward, x: xy.x, y: xy.y });
    if (m.kind === 'healer') {
      this.pools.push({
        id: this.nextId++,
        side: m.side,
        lane: m.lane,
        pos: m.pos,
        x: xy.x,
        y: xy.y,
        radius: m.special.poolRadius ?? 50,
        heal: m.special.poolHeal ?? 5,
        timer: 0,
        duration: m.special.poolDuration ?? 4,
        tick: 0,
      });
    }
  }

  private updateProjectiles(dt: number): void {
    const alive = new Map<number, MinionState>();
    for (const m of this.minions) if (m.hp > 0) alive.set(m.id, m);
    const keep: ProjectileState[] = [];
    for (const p of this.projectiles) {
      if (p.kind === 'mortar') {
        p.t += dt;
        const k = clamp(p.t / p.duration, 0, 1);
        p.x = p.sx + (p.tx - p.sx) * k;
        p.y = p.sy + (p.ty - p.sy) * k - Math.sin(k * Math.PI) * 70;
        if (k >= 1) {
          this.events.push({ type: 'explosion', x: p.tx, y: p.ty, radius: p.splash, kind: 'mortar' });
          this.splashDamage(p, p.tx, p.ty);
        } else keep.push(p);
        continue;
      }
      const found = alive.get(p.targetId);
      const target = found && found.hp > 0 ? found : undefined;
      if (target) {
        const xy = minionXY(target);
        p.tx = xy.x;
        p.ty = xy.y;
      } else if (p.splash <= 0) {
        // Target gone and nothing to splash: fizzle.
        continue;
      }
      const dx = p.tx - p.x;
      const dy = p.ty - p.y;
      const d = Math.hypot(dx, dy);
      const stepLen = p.speed * dt;
      p.rotation = Math.atan2(dy, dx);
      if (d <= stepLen + 6) {
        p.x = p.tx;
        p.y = p.ty;
        if (p.splash > 0) {
          const kind = p.kind === 'fire' || p.kind === 'fireball' ? 'fire' : p.kind === 'frost' ? 'frost' : 'blast';
          this.events.push({ type: 'explosion', x: p.x, y: p.y, radius: p.splash, kind });
          this.splashDamage(p, p.x, p.y);
        } else if (target) {
          this.applyDamage(target, p.damage, p.side, p.kind);
          if (p.slow > 0) {
            target.slowFactor = Math.min(target.slowFactor, p.slow);
            target.slowTimer = Math.max(target.slowTimer, p.slowDuration);
          }
          if (p.weaken > 0) {
            target.weakFactor = Math.min(target.weakFactor, 1 - p.weaken);
            target.weakTimer = Math.max(target.weakTimer, p.weakenDuration);
          }
          // Arrow pierce: continue to a second target behind the first.
          if (p.kind === 'arrow' && !p.pierced && p.pierceChance > 0 && this.rng.chance(p.pierceChance)) {
            const next = this.minions.find((m) => m.hp > 0 && m.side === target.side && m.lane === target.lane && m.id !== target.id && m.pos < target.pos + 60 && m.pos > target.pos - 60);
            if (next) {
              p.pierced = true;
              p.targetId = next.id;
              p.damage *= 0.7;
              keep.push(p);
            }
          }
        }
        continue;
      }
      p.x += (dx / d) * stepLen;
      p.y += (dy / d) * stepLen;
      p.t += dt;
      if (p.t < 6) keep.push(p);
    }
    this.projectiles = keep;
  }

  private splashDamage(p: ProjectileState, x: number, y: number): void {
    for (const m of this.minions) {
      if (m.side === p.side || m.hp <= 0) continue;
      const xy = minionXY(m);
      const d = Math.hypot(xy.x - x, xy.y - y);
      if (d <= p.splash + 10 * m.scale) {
        const falloff = d < p.splash * 0.5 ? 1 : 0.65;
        this.applyDamage(m, p.damage * falloff, p.side, p.kind);
        if (p.slow > 0) {
          m.slowFactor = Math.min(m.slowFactor, p.slow);
          m.slowTimer = Math.max(m.slowTimer, p.slowDuration);
        }
      }
    }
  }

  private updatePools(dt: number): void {
    const keep: PoolState[] = [];
    for (const pool of this.pools) {
      pool.timer += dt;
      pool.tick += dt;
      if (pool.tick >= 0.5) {
        pool.tick -= 0.5;
        for (const m of this.minions) {
          if (m.side !== pool.side || m.lane !== pool.lane || m.hp <= 0) continue;
          if (Math.abs(m.pos - pool.pos) <= pool.radius && m.hp < m.maxHp) {
            const amount = Math.min(m.maxHp - m.hp, pool.heal * 0.5);
            m.hp += amount;
          }
        }
      }
      if (pool.timer < pool.duration) keep.push(pool);
    }
    this.pools = keep;
  }

  private checkEnd(): void {
    const [a, b] = this.players;
    let winner: Side | -1 | null = null;
    let reason = '';
    if (a.hearts <= 0 && b.hearts <= 0) {
      winner = -1;
      reason = 'Both fortresses fell';
    } else if (a.hearts <= 0) {
      winner = 1;
      reason = 'Your fortress fell';
    } else if (b.hearts <= 0) {
      winner = 0;
      reason = 'Enemy fortress destroyed';
    } else if (this.time >= this.rules.hardEndAt) {
      if (a.hearts !== b.hearts) {
        winner = a.hearts > b.hearts ? 0 : 1;
        reason = 'Time up - more hearts remaining';
      } else if (a.stats.damageDealt !== b.stats.damageDealt) {
        winner = a.stats.damageDealt > b.stats.damageDealt ? 0 : 1;
        reason = 'Time up - more damage dealt';
      } else {
        winner = -1;
        reason = 'Time up - draw';
      }
    }
    if (winner !== null) {
      this.ended = true;
      this.winner = winner;
      this.endReason = reason;
      this.events.push({ type: 'end', winner, reason });
    }
  }

  /** Drain events accumulated since the last call (renderer / audio consume them). */
  drainEvents(): SimEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }
}
